import { Popover as ArkPopover } from "@ark-ui/solid";
import { IconButton } from "../button/Button";
import { omit } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import { type Component, type ParentComponent, Show } from "solid-js";
import type { PositioningOptions } from "@zag-js/popper";
import { createWritableMemo } from "@solid-primitives/memo";
import Portal from "../../utils/Portal";

type PopoverProps = {
  variant: "md" | "lg";
  Title: Component;
  "aria-label": string;
  Trigger: Component<Omit<ArkPopover.TriggerProps, "children">>;
  positioning?: PositioningOptions;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
};

const Popover: ParentComponent<PopoverProps> = (props) => {
  const { t } = useTranslation();
  const [open, setOpen] = createWritableMemo(() => props.open ?? false);

  return (
    <ArkPopover.Root
      open={open()}
      onOpenChange={(state) => {
        setOpen(state.open);
        props.onOpenChange?.(state.open);
      }}
      closeOnEscape
      closeOnInteractOutside
      lazyMount
      unmountOnExit
      modal={props.modal ?? true}
      positioning={{ gutter: 4, ...props.positioning }}
    >
      <ArkPopover.Trigger asChild={(triggerProps) => <props.Trigger {...omit(triggerProps(), "children")} />} />
      <Show when={open()}>
        <Portal>
          <ArkPopover.Positioner class="select-auto">
            <ArkPopover.Content
              aria-label={props["aria-label"]}
              class="z-10 flex max-h-136 animate-popover-open flex-col rounded-lg bg-surface-default pt-2 shadow-elevation-light-3"
              classList={{
                "w-88": props.variant === "md",
                "w-120": props.variant === "lg",
              }}
            >
              <div class="flex w-full items-center justify-between gap-1.5 pr-2 pl-4">
                <ArkPopover.Title aria-label={props["aria-label"]} class="flex min-w-0">
                  <props.Title />
                </ArkPopover.Title>
                <ArkPopover.CloseTrigger
                  asChild={(parentProps) => (
                    <IconButton
                      {...omit(parentProps(), "children")}
                      icon="x"
                      aria-label={t(($) => $.CLOSE_ARIA)}
                      size={props.variant === "md" ? "medium" : "large"}
                      variant="tertiary"
                    />
                  )}
                />
              </div>
              <ArkPopover.Description
                class="flex flex-col overflow-hidden pb-4"
                classList={{
                  "px-4": props.variant === "md",
                }}
              >
                {props.children}
              </ArkPopover.Description>
            </ArkPopover.Content>
          </ArkPopover.Positioner>
        </Portal>
      </Show>
    </ArkPopover.Root>
  );
};

export default Popover;
